import type { WasteSenseEventRow, WasteSenseSite } from "./types";

/**
 * Confidence threshold for the priority predicate. Mirrors
 * `is_priority_detection` in the legacy generators
 * (`wastesense/.../generate_..._report.py`):
 *
 *     return detected and conf > 0.95
 *
 * Kept inline so a casual reader of this file sees the contract
 * without chasing into Python.
 */
const PRIORITY_CONFIDENCE_THRESHOLD = 0.95;

/**
 * Potential-incident predicate from the legacy generator's
 * `is_potential_detection(row)`:
 *
 *     return row.get("detected") is True
 *
 * "AI flagged this as potential dumping" — any confidence will do.
 * The strict `is_priority_detection` filter then narrows that pool
 * to the customer-safe rollup actually surfaced to councils.
 */
export function isPotentialIncident(row: WasteSenseEventRow): boolean {
  return row.detected === true;
}

/**
 * Priority-incident predicate. Strict, exact mirror of the legacy
 * Python:
 *
 *     def is_priority_detection(row):
 *         detected = row.get("detected") is True
 *         conf = float(row.get("confidence", 0.0) or 0.0)
 *         return detected and conf > 0.95
 *
 * Rows without `detected`/`confidence` are not priority — same as
 * legacy. We deliberately do NOT fall back to description mining:
 * the legacy generators define `is_dd_detection` but never call it,
 * and using it as a fallback inflates priority counts on real
 * captured fixtures (e.g. Merri-bek, where every row's description
 * starts with "no dumping detected" and the legacy report shows
 * 0 priorities). The renderer must agree with what the upstream
 * pipeline says, not second-guess it from prose.
 */
export function isPriorityIncident(row: WasteSenseEventRow): boolean {
  if (row.detected !== true) return false;
  if (typeof row.confidence !== "number") return false;
  return row.confidence > PRIORITY_CONFIDENCE_THRESHOLD;
}

/**
 * Returns the priority subset of a site's rows, newest-first. Used
 * by the hero list to render snapshots + descriptions for every
 * priority incident in the window.
 */
export function priorityRowsForSite(site: WasteSenseSite): readonly WasteSenseEventRow[] {
  const rows = (site.rows ?? []).filter(isPriorityIncident);
  return [...rows].sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
}

/**
 * Counts priority incidents for a single site.
 *
 * Rows are the canonical source — the count returned here always
 * equals the number of rows the priority hero list will render for
 * this site, so KPIs, map badges, per-site cards, and the hero list
 * cannot disagree.
 *
 * The `dd_detections` rollup is used only as a *fallback* when the
 * payload didn't ship row-level data (e.g. a summary-only response).
 * In that case the hero list is naturally empty and the rollup is
 * the only number we can show, so there's no contradiction. As soon
 * as rows are present, we prefer them: a payload where
 * `dd_detections` disagrees with the rows is a backend integrity
 * problem we want to surface, not paper over by trusting one number
 * in the KPI strip and a different one in the hero.
 *
 * The legacy Python generator preferred `detected_high_conf_events`
 * because its row table was truncated server-side while the rollup
 * was computed pre-truncation. Our renderer doesn't truncate (the
 * random generator preserves all priority rows via
 * `limitRowsKeepingPriority`, and real payloads ship un-truncated
 * row data), so the original reason to prefer the rollup doesn't
 * apply here.
 */
export function countPrioritySiteIncidents(site: WasteSenseSite): number {
  if (Array.isArray(site.rows)) {
    return site.rows.reduce((acc, row) => (isPriorityIncident(row) ? acc + 1 : acc), 0);
  }
  return site.dd_detections ?? 0;
}

/**
 * Counts AI-flagged potential incidents for a single site (pre
 * customer-safe review).
 *
 * Rows are the canonical source — same shape as
 * `countPrioritySiteIncidents`, just with the looser
 * `isPotentialIncident` predicate. The `potential_incidents` rollup
 * is used only as a fallback when no rows are supplied.
 */
export function countPotentialSiteIncidents(site: WasteSenseSite): number {
  if (Array.isArray(site.rows)) {
    return site.rows.reduce((acc, row) => (isPotentialIncident(row) ? acc + 1 : acc), 0);
  }
  return site.potential_incidents ?? 0;
}

/**
 * Convenience: total priority incidents across every site in the
 * report. Mirrors the `total_dd_detections` rollup the legacy
 * generator feeds into the AI overview KPI.
 */
export function countPriorityIncidents(sites: readonly WasteSenseSite[]): number {
  return sites.reduce((acc, s) => acc + countPrioritySiteIncidents(s), 0);
}

/**
 * Convenience: total potential incidents across every site in the
 * report. Mirrors `overall_summary.detected_true_events` in the
 * legacy generator's KPI strip.
 */
export function countPotentialIncidents(sites: readonly WasteSenseSite[]): number {
  return sites.reduce((acc, s) => acc + countPotentialSiteIncidents(s), 0);
}

/**
 * Aggregates every priority incident in the report into one
 * newest-first list, with each row tagged by its site so the hero
 * can render "Site title — timestamp — description" without an extra
 * lookup loop. Sites are visited in the order they were passed in
 * (callers typically pre-sort via `sortSites`).
 */
export interface PriorityIncident {
  site: WasteSenseSite;
  row: WasteSenseEventRow;
}

export function collectPriorityIncidents(sites: readonly WasteSenseSite[]): readonly PriorityIncident[] {
  const out: PriorityIncident[] = [];
  for (const site of sites) {
    for (const row of site.rows ?? []) {
      if (isPriorityIncident(row)) out.push({ site, row });
    }
  }
  out.sort((a, b) => (a.row.timestamp < b.row.timestamp ? 1 : -1));
  return out;
}
